import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, maskMoneyInput, parseInputToApi, moneyToInput } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import { Modal, Field } from './TransactionForm';
import type { Transaction, ReferenceOption } from './types';

/**
 * Baixa de lançamento — RF-LAN-04.
 *
 * O valor vem preenchido com o saldo em aberto, que é o caso mais comum.
 * Editá-lo para menos gera baixa parcial e a situação vira "parcialmente pago"
 * automaticamente — o servidor deriva isso da soma das baixas (RN-02).
 */
export function SettleDialog({
  transaction,
  accounts,
  cards = [],
  transactionEntityId,
  selectedIds,
  onClose,
  onSettled,
  onOpenAccounts,
}: {
  transaction: Transaction;
  /**
   * TODAS as contas do usuário, não só as da entidade do lançamento — RN-13.
   *
   * Um cliente pode pagar um título da Empresa na conta da PF, e pagar uma
   * conta da Empresa do bolso da PF é igualmente comum. Filtrar aqui obrigava a
   * registrar uma mentira (baixar numa conta que não recebeu) ou a não
   * registrar nada.
   */
  accounts: Array<ReferenceOption & { entity_id: number; entity_name: string }>;
  /**
   * Os cartões, para pagar a conta NO CARTÃO — RF-PAG-06.
   *
   * Só da entidade do título, ao contrário das contas. Conta de outra entidade
   * é dinheiro emprestado, e o saldo entre as duas registra isso. Cartão de
   * outra entidade colocaria a dívida na fatura de quem não a contraiu — e
   * fatura é obrigação com o banco, não acerto entre duas pessoas.
   */
  cards?: Array<ReferenceOption & { entity_id: number; entity_name: string; last4: string | null }>;
  /** Entidade dona do título: define qual grupo vem primeiro e quando avisar. */
  transactionEntityId?: number;
  selectedIds: number[];
  onClose: () => void;
  onSettled: () => void;
  onOpenAccounts?: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);

  const remainingMasked = moneyToInput(transaction.remaining_amount);

  const donoId = transactionEntityId ?? transaction.entity_id;

  /**
   * As contas da entidade do título vêm primeiro.
   *
   * O caso comum continua sendo receber na própria entidade — ele não pode
   * ficar mais difícil por causa do caso raro.
   */
  const daEntidade = accounts.filter((a) => a.entity_id === donoId);
  const deOutras = accounts.filter((a) => a.entity_id !== donoId);

  const [accountId, setAccountId] = useState<string>(
    String(transaction.account_id ?? daEntidade[0]?.id ?? accounts[0]?.id ?? ''),
  );

  /** A conta escolhida, quando for de outra entidade — dispara o aviso. */
  const contaDeOutraEntidade = deOutras.find((a) => String(a.id) === accountId) ?? null;
  /**
   * DE ONDE SAIU: conta ou cartão — RF-PAG-06.
   *
   * "Quero a opção de pagar no cartão de crédito, aí ele lista o cartão
   * cadastrado, e essa pendência é adicionada no cartão."
   *
   * Um seletor só, porque são mutuamente exclusivos — o banco exige exatamente
   * um dos dois (`chk_fin_settl_container`). Dois campos lado a lado
   * convidariam a preencher ambos e receber um erro que não explica por quê.
   */
  /**
   * TODOS os cartões, como já era com as contas.
   *
   * A primeira versão filtrava pela entidade do título, com o argumento de que
   * cartão de outra entidade poria a dívida na fatura de quem não a contraiu.
   * O argumento estava certo e a conclusão, errada — vale igualzinho para
   * CONTA de outra entidade, que o sistema oferece desde sempre e que este
   * usuário usa toda semana.
   *
   * O relato: *"ao selecionar o cartão de crédito não aparecem todos, só
   * aparece o Porto"*. Era uma conta da LUANA, e o Porto dela é o único cartão
   * daquela entidade — os outros quatro são do Fabio.
   *
   * Filtrar obrigava a escolher entre registrar uma mentira e não registrar.
   */
  const cartoesDaEntidade = cards.filter((c) => c.entity_id === donoId);
  const cartoesDeOutras = cards.filter((c) => c.entity_id !== donoId);


  const [origem, setOrigem] = useState<'conta' | 'cartao'>('conta');
  const [cardId, setCardId] = useState<string>(
    String(cartoesDaEntidade[0]?.id ?? cards[0]?.id ?? ''),
  );
  const [parcelas, setParcelas] = useState(1);

  /**
   * DEPOIS de `cardId`, e isto quebrou a tela uma vez.
   *
   * Esta linha estava ACIMA do `useState` que declara `cardId`. O TypeScript
   * não reclamou: a referência está dentro de um callback, e ele trata callback
   * como possivelmente adiado. Mas `.find()` executa na hora — então em tempo
   * de execução dava `Cannot access 'cardId' before initialization`, o
   * componente lançava e o diálogo de pagamento simplesmente não abria.
   *
   * `tsc --noEmit` passou, `vite build` passou, e os 45 testes passaram. Quem
   * pegou foi o usuário clicando em "pagar".
   */
  const cartaoDeOutraEntidade = cartoesDeOutras.find((c) => String(c.id) === cardId) ?? null;

  const noCartao = origem === 'cartao' && cardId !== '';

  const [paidAt, setPaidAt] = useState(today);
  const [amountMasked, setAmountMasked] = useState(remainingMasked);
  const [method, setMethod] = useState('pix');

  /**
   * O campo pergunta QUANTO SAIU DA CONTA — RF-LAN-15.
   *
   * "Pego com um pouco de juros que não sei informar ao certo." A pessoa não
   * sabe o juro; sabe o que o banco cobrou, que está no extrato. Pedir o juro
   * a obrigaria a calcular 530 − 500 de cabeça para o sistema refazer a conta
   * ao contrário.
   */
  const amount = parseInputToApi(amountMasked);
  const devido = Number(transaction.remaining_amount);
  const pago = Number(amount);

  const juros = pago > devido ? pago - devido : 0;
  const faltando = pago < devido && pago > 0 ? devido - pago : 0;

  /**
   * Pagou MENOS: parcial ou desconto?
   *
   * São resultados opostos — um mantém a dívida, o outro a extingue. O padrão
   * é parcial, o menos destrutivo: errar para "ainda deve" se percebe e se
   * corrige; errar para "quitado" some com a cobrança em silêncio.
   */
  const [quitarComDesconto, setQuitarComDesconto] = useState(false);

  /**
   * ================================================================
   * O SALDO DÁ PARA ESTE PAGAMENTO? — RN-82, RF-PAG-07
   * ================================================================
   *
   * O sistema aceitava qualquer baixa sem olhar se havia dinheiro. Numa conta
   * com limite de cheque especial ZERO, pagar uma conta a deixava negativa em
   * silêncio — e ficar negativo sem limite significa uma de três coisas, todas
   * merecendo atenção: falta lançar uma entrada, o pagamento saiu de outra
   * conta, ou o limite existe e não foi cadastrado.
   *
   * AVISA, NÃO IMPEDE. Bloquear seria pior: o saldo pode estar negativo
   * justamente porque ainda falta lançar coisa — foi o que aconteceu com as
   * liberações de dívida nesta mesma base. Recusar impediria de registrar um
   * pagamento que existiu de verdade.
   *
   * A conta é do SERVIDOR (`/payables/simulate`), e não daqui: dinheiro não se
   * calcula em JavaScript (ADR-0003). O endpoint já existia e nenhuma tela o
   * chamava.
   */
  const simulacao = useQuery({
    queryKey: ['simulate', transaction.id, accountId, amount],
    enabled: accountId !== '' && Number(amount) > 0,
    queryFn: () =>
      api.post<{
        data: {
          account_name: string;
          current_balance: MoneyString;
          balance_after: MoneyString;
          overdraft_limit: MoneyString;
          goes_negative: boolean;
          uses_overdraft: boolean;
        };
      }>(
        '/payables/simulate',
        { transaction_ids: [transaction.id], account_id: Number(accountId), amount },
        { entityIds: selectedIds },
      ),
    select: (r) => r.data,
    // Aviso não precisa de dado fresco a cada tecla: a chave já muda com o
    // valor, e reconsultar em foco só gastaria requisição.
    refetchOnWindowFocus: false,
  });

  const sim = simulacao.data;

  const settle = useMutation({
    mutationFn: () =>
      api.post(`/transactions/${transaction.id}/settle`, {
        /**
         * Manda UM dos dois, nunca os dois.
         *
         * No cartão não vai `paid_amount`: ele existe para separar juros de
         * atraso do valor do título, e isso é conversa entre você e o banco da
         * conta. A dívida que entra no cartão é o valor do título.
         */
        ...(noCartao
          ? { credit_card_id: Number(cardId), installments: parcelas }
          : { account_id: Number(accountId), paid_amount: amount, settle_full: quitarComDesconto }),
        paid_at: paidAt,
        /**
         * `paid_amount`, e não `amount`.
         *
         * `amount` é quanto do TÍTULO se baixa; `paid_amount` é quanto saiu da
         * conta. Com juros os dois divergem, e é o servidor que separa — a
         * conta de dinheiro não se faz aqui (ADR-0003).
         */
        payment_method: noCartao ? 'cartao_credito' : method,
      }, { entityIds: selectedIds }),
    onSuccess: onSettled,
  });

  const error = settle.error instanceof ApiError ? settle.error : null;
  const isReceita = transaction.type === 'receita';

  return (
    <Modal title={isReceita ? 'Registrar recebimento' : 'Registrar pagamento'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); settle.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.entries(error.errors).map(([f, m]) => <li key={f}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="rounded-lg bg-slate-50 px-3.5 py-3 text-sm dark:bg-slate-800/50">
          <p className="font-medium">{transaction.description}</p>
          <div className="mt-2 space-y-0.5 text-slate-600 dark:text-slate-400">
            <div className="flex justify-between">
              <span>Valor do lançamento</span>
              <span>{formatMoney(transaction.net_amount)}</span>
            </div>
            {transaction.settled_amount !== '0.0000' && (
              <div className="flex justify-between">
                <span>Já {isReceita ? 'recebido' : 'pago'}</span>
                <span>{formatMoney(transaction.settled_amount)}</span>
              </div>
            )}
            <div className="flex justify-between font-medium text-slate-900 dark:text-slate-100">
              <span>Em aberto</span>
              <span>{formatMoney(transaction.remaining_amount)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={isReceita ? 'Valor recebido' : 'Valor pago'} required>
            <input
              value={amountMasked}
              onChange={(e) => setAmountMasked(maskMoneyInput(e.target.value))}
              required
              inputMode="numeric"
              autoFocus
              className="input w-full text-right"
            />
          </Field>
          <Field label="Data" required>
            <input
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              required
              className="input w-full"
            />
          </Field>
        </div>

        {/**
          * ================================================================
          * DE ONDE SAIU — CONTA OU CARTÃO (RF-PAG-06)
          * ================================================================
          *
          * "Quero a opção de pagar no cartão de crédito, aí ele lista o cartão
          * cadastrado, e essa pendência é adicionada no cartão."
          *
          * Só aparece em DESPESA e quando há cartão: o cartão é passivo, e
          * "receber" nele significaria aumentar a dívida para registrar uma
          * entrada. Oferecer a opção e depois recusá-la seria pior que não
          * oferecer.
          */}
        {!isReceita && cards.length > 0 && (
          <div className="flex gap-2">
            {([
              ['conta', 'Conta bancária'],
              ['cartao', 'Cartão de crédito'],
            ] as const).map(([valor, rotulo]) => (
              <button
                key={valor}
                type="button"
                onClick={() => setOrigem(valor)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  origem === valor
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300'
                    : 'border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800'
                }`}
              >
                {rotulo}
              </button>
            ))}
          </div>
        )}

        {noCartao ? (
          <>
            <Field label="Cartão" required>
              <select
                value={cardId}
                onChange={(e) => setCardId(e.target.value)}
                required
                className="input w-full"
              >
                {/* Os da própria entidade primeiro e soltos; os outros num
                    grupo com o nome de quem é. O caso comum não pode ficar
                    mais difícil por causa do raro. */}
                {cartoesDaEntidade.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.last4 ? ` ····${c.last4}` : ''}
                  </option>
                ))}
                {cartoesDeOutras.length > 0 && (
                  <optgroup label="Cartões de outra entidade">
                    {cartoesDeOutras.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.last4 ? ` ····${c.last4}` : ''} · {c.entity_name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </Field>

            <Field
              label="Parcelas"
              hint="a conta é quitada hoje; o cartão cobra mês a mês"
            >
              <select
                value={parcelas}
                onChange={(e) => setParcelas(Number(e.target.value))}
                className="input w-full"
              >
                {Array.from({ length: 24 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n === 1 ? 'À vista' : `${n}x`}
                  </option>
                ))}
              </select>
            </Field>

            {/**
              * A CONSEQUÊNCIA, DITA ANTES DE APLICAR.
              *
              * Duas coisas surpreendem quem faz isto pela primeira vez: a conta
              * some de A pagar e o saldo do banco não muda. Sem esta frase,
              * parece dinheiro evaporando.
              */}
            <p className="rounded-lg bg-indigo-50 px-3.5 py-3 text-xs text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-200">
              A conta fica <strong>quitada hoje</strong> — o fornecedor recebeu. O valor vira{' '}
              <strong>dívida no cartão</strong> e sai do banco quando a fatura vencer.
              {parcelas > 1 && (
                <span className="mt-1 block">
                  Em {parcelas}x, cada parcela entra na fatura do seu mês.
                </span>
              )}
              <span className="mt-1 block text-indigo-800/70 dark:text-indigo-300/70">
                A despesa continua contada uma vez só, no lançamento — o cartão recebe dívida, não
                gasto novo.
              </span>
              {cartaoDeOutraEntidade && (
                <span className="mt-1.5 block font-medium">
                  A dívida fica no cartão de {cartaoDeOutraEntidade.entity_name}, e o lançamento
                  continua sendo desta entidade.
                </span>
              )}
            </p>
          </>
        ) : (
          <Field label="Conta" required>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              required
              disabled={accounts.length === 0}
              className="input w-full"
            >
              {accounts.length === 0 && <option value="">Nenhuma conta disponível</option>}
              {daEntidade.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              {deOutras.length > 0 && (
                <optgroup label="Contas de outra entidade">
                  {deOutras.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} · {a.entity_name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </Field>
        )}

        {/**
          * CONTA DE OUTRA ENTIDADE: registra, e diz o que está registrando.
          *
          * Isto acontece de verdade — o cliente paga um título da Empresa na
          * conta da PF, ou você paga uma conta da Empresa do bolso da PF.
          * Antes a lista só trazia as contas da entidade do título, e a pessoa
          * tinha de escolher entre registrar uma mentira ou não registrar.
          *
          * O que o aviso impede é o silêncio: sem ele, o caixa de uma entidade
          * subiria e o da outra não, sem nada ligando as duas pontas. Com ele,
          * a pessoa sabe que fica um saldo entre as entidades — e sabe como
          * zerá-lo.
          */}
        {contaDeOutraEntidade && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-3 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
            <p className="font-medium text-indigo-900 dark:text-indigo-200">
              O dinheiro vai para a conta de {contaDeOutraEntidade.entity_name}.
            </p>
            <p className="mt-1 text-indigo-800/80 dark:text-indigo-300/80">
              O título continua sendo de <strong>{transaction.entity_name ?? 'outra entidade'}</strong>,
              e fica registrado que {contaDeOutraEntidade.entity_name} está com esse dinheiro. O
              saldo aparece no painel e some quando você fizer a transferência de{' '}
              <strong>reembolso</strong> entre as duas.
            </p>
          </div>
        )}

        {/* ================================================================
            LISTA VAZIA NÃO É DEFEITO — É FALTA DE CADASTRO, E PRECISA DIZER
            ================================================================

            Um seletor vazio, sem explicação, é indistinguível de tela quebrada.
            Quem tem uma conta cadastrada e não a vê aqui conclui que o sistema
            perdeu o cadastro. */}
        {accounts.length === 0 && (
          <div className="rounded-lg bg-amber-50 px-3.5 py-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <p className="font-medium">
              {transaction.entity_name ?? 'Esta entidade'} não tem conta cadastrada.
            </p>
            <p className="mt-1 text-xs">
              A lista mostra apenas as contas desta entidade — o dinheiro dela não pode entrar na
              conta de outra, senão os dois saldos passam a mentir.
            </p>
            <p className="mt-1.5 text-xs">
              Cadastre uma conta para <strong>{transaction.entity_name ?? 'esta entidade'}</strong>{' '}
              {onOpenAccounts ? (
                <button
                  type="button"
                  onClick={onOpenAccounts}
                  className="font-medium underline underline-offset-2"
                >
                  em Contas
                </button>
              ) : (
                <>em <strong>Contas</strong>, no menu lateral</>
              )}
              . Se ela já existe em outra entidade, o caminho é uma transferência entre entidades,
              não uma baixa aqui.
            </p>
          </div>
        )}

        {/* No cartão a forma JÁ está escolhida: é o cartão.
            Deixá-la visível oferecia "Pix" e "Boleto" logo abaixo de "Cartão de
            crédito", e o que fosse escolhido ali era ignorado no envio — campo
            que não faz nada é pior que campo ausente. */}
        {!noCartao && (
          <Field label="Forma">
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="input w-full">
              <option value="pix">Pix</option>
              <option value="boleto">Boleto</option>
              <option value="ted">TED</option>
              <option value="debito_automatico">Débito automático</option>
              <option value="dinheiro">Dinheiro</option>
              <option value="cartao_debito">Cartão de débito</option>
            </select>
          </Field>
        )}

        {/*
          ================================================================
          PAGOU A MAIS — o excesso é juros, e o sistema mostra a conta
          ================================================================

          Antes isto era RECUSADO: "baixa de R$ 530,00 excede o saldo em
          aberto". A única saída era editar o título para R$ 530 — e aí os
          juros viravam despesa da categoria original.

          O resumo mostra a separação ANTES de salvar, porque ela cria um
          lançamento novo. Quem paga precisa saber disso agora, e não encontrar
          "Juros/multa — Energia" no extrato depois sem entender de onde veio.
        */}
        {juros > 0 && (
          <div className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <div className="flex justify-between">
              <span>Valor {isReceita ? 'do título' : 'da conta'}</span>
              <strong className="tabular-nums">{formatMoney(transaction.remaining_amount)}</strong>
            </div>
            <div className="mt-0.5 flex justify-between">
              <span>Juros/multa</span>
              <strong className="tabular-nums">+ {formatMoney(juros.toFixed(2))}</strong>
            </div>
            <div className="mt-1 flex justify-between border-t border-amber-300/60 pt-1 dark:border-amber-800/60">
              <span>{isReceita ? 'Entra na conta' : 'Sai da conta'}</span>
              <strong className="tabular-nums">{formatMoney(amount)}</strong>
            </div>
            <p className="mt-1.5 text-xs">
              Os juros viram um lançamento próprio, em “Juros e multa por atraso”. A categoria
              original fica com o valor {isReceita ? 'do título' : 'da conta'}.
            </p>
          </div>
        )}

        {/*
          O AVISO DE SALDO, com os números.

          "Saldo insuficiente" sozinho não ajuda a decidir. Mostrar quanto há,
          quanto sai e quanto sobra deixa claro se é erro de digitação, conta
          errada, ou lançamento que falta.

          Só aparece em DESPESA: receita entrando nunca deixa a conta negativa.
        */}
        {!isReceita && sim?.goes_negative && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
            <p className="font-medium">
              A conta {sim.account_name} ficaria em {formatMoney(sim.balance_after)}
            </p>

            <div className="mt-1.5 space-y-0.5 text-xs">
              <div className="flex justify-between">
                <span>Saldo hoje</span>
                <span className="tabular-nums">{formatMoney(sim.current_balance)}</span>
              </div>
              <div className="flex justify-between">
                <span>Este pagamento</span>
                <span className="tabular-nums">− {formatMoney(amount)}</span>
              </div>
              <div className="flex justify-between border-t border-amber-300/60 pt-0.5 font-medium dark:border-amber-800/60">
                <span>Ficaria</span>
                <span className="tabular-nums">{formatMoney(sim.balance_after)}</span>
              </div>
            </div>

            <p className="mt-1.5 text-xs">
              {sim.uses_overdraft
                ? `Entra no limite de cheque especial de ${formatMoney(sim.overdraft_limit)}.`
                : 'Esta conta não tem limite de cheque especial cadastrado — o banco recusaria.'}
            </p>

            {/*
              O caminho de saída, e não só o aviso. Ficar negativo sem limite
              quase sempre é dado faltando, não pagamento errado.
            */}
            {!sim.uses_overdraft && (
              <p className="mt-1 text-xs opacity-80">
                Se o dinheiro saiu de outra conta, troque acima. Se falta lançar uma entrada, dá
                para registrar mesmo assim e acertar depois.
              </p>
            )}
          </div>
        )}

        {/*
          PAGOU A MENOS — e aqui o sistema NÃO adivinha.

          Parcial e desconto levam a resultados opostos: um mantém a cobrança,
          o outro a extingue. Perguntar custa um clique; errar sozinho custa uma
          dívida que some ou uma que insiste.
        */}
        {faltando > 0 && (
          <div className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <p>
              Você {isReceita ? 'recebeu' : 'pagou'}{' '}
              <strong>{formatMoney(faltando.toFixed(2))}</strong> a menos.
            </p>

            <label className="mt-2 flex cursor-pointer items-start gap-2">
              <input
                type="radio"
                checked={!quitarComDesconto}
                onChange={() => setQuitarComDesconto(false)}
                className="mt-0.5"
              />
              <span>
                Pagamento parcial
                <span className="block text-xs opacity-80">
                  ainda faltam {formatMoney(faltando.toFixed(2))} — o título continua em aberto
                </span>
              </span>
            </label>

            <label className="mt-1 flex cursor-pointer items-start gap-2">
              <input
                type="radio"
                checked={quitarComDesconto}
                onChange={() => setQuitarComDesconto(true)}
                className="mt-0.5"
              />
              <span>
                Desconto — considerar quitado
                <span className="block text-xs opacity-80">
                  os {formatMoney(faltando.toFixed(2))} viram receita financeira
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={settle.isPending || Number(amount) <= 0 || !accountId}
            className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {settle.isPending ? 'Registrando…' : 'Confirmar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
